// src/components/BookingCard.jsx
// Renders a single booking card with role-aware badges for different roles.

const parseBookingDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const formatDateTime = (value) => {
  const d = parseBookingDate(value);
  if (!d) return "";
  return d.toLocaleString();
};

const formatMoney = (value) => {
  if (value == null) return "$0.00";
  const n = Number(value);
  if (Number.isNaN(n)) return `$${value}`;
  return `$${n.toFixed(2)}`;
};

const getSalonName = (b) =>
  b.salon_name ||
  b.salon?.name ||
  b.booth?.salon?.name ||
  b.booth?.salon_name ||
  "Unknown salon";

const getBoothName = (b) =>
  b.booth_name || b.booth?.booth_name || b.booth?.name || "Booth";

const getStatus = (b) => (b.status || "confirmed").toLowerCase();

export default function BookingCard({
  booking,
  role,
  currentUserId,
  adminMyBookingIds, // may be passed from parent; we just touch it to avoid eslint warning
  showCancelButton,
  onCancel,
}) {
  const b = booking || {};
  const isHost = role === "host";

  // Touch adminMyBookingIds so eslint doesn't mark it unused if provided
  if (adminMyBookingIds && adminMyBookingIds.size === -1) {
    // no-op
  }

  // -----------------------------
  // Ownership / context flags
  // -----------------------------

  // Backend flags if available
  const backendIsMine =
    typeof b.is_mine === "boolean"
      ? b.is_mine
      : typeof b.isMine === "boolean"
      ? b.isMine
      : undefined;

  const backendIsAtMySalon =
    typeof b.is_at_my_salon === "boolean"
      ? b.is_at_my_salon
      : typeof b.isAtMySalon === "boolean"
      ? b.isAtMySalon
      : undefined;

  // Fallback IDs when backend flags are not present
  const bookedByUserId = Number(
    b.technician_id ??
      b.technicianId ??
      b.user_id ??
      b.userId ??
      b.owner_id ??
      b.ownerId ??
      b.client_id ??
      b.clientId ??
      0
  );

  const boothOwnerId = Number(
    b.booth?.owner_id ??
      b.booth?.ownerId ??
      b.booth_owner_id ??
      b.boothOwnerId ??
      0
  );

  // Computed fallbacks
  const computedIsMine =
    bookedByUserId && currentUserId
      ? bookedByUserId === currentUserId
      : false;

  const computedIsAtMySalon =
    isHost && boothOwnerId && currentUserId
      ? boothOwnerId === currentUserId
      : false;

  // === STRICT "My booking" rules for ALL roles ===
  // - Prefer backend is_mine
  // - Otherwise, when the booked-by user matches currentUserId
  let isMyBooking = false;
  if (typeof backendIsMine === "boolean") {
    isMyBooking = backendIsMine;
  } else {
    isMyBooking = computedIsMine;
  }

  // Resolve an "at my salon" flag (hosts only really care)
  const isAtMySalon =
    typeof backendIsAtMySalon === "boolean"
      ? backendIsAtMySalon
      : computedIsAtMySalon;

  // === Host-only context label ===
  // - isAtMySalon === true  && !isMyBooking -> "Booking at my salon"
  // - isAtMySalon === false &&  isMyBooking -> "My booking at another salon"
  //   (only possible if a host books at someone else's salon)
  let hostContextLabel = "";
  if (isHost) {
    if (isAtMySalon && !isMyBooking) {
      hostContextLabel = "Booking at my salon";
    } else if (!isAtMySalon && isMyBooking) {
      hostContextLabel = "My booking at another salon";
    }
  }

  // -----------------------------
  // Add-ons
  // -----------------------------
  const selectedAddons =
    (Array.isArray(b.selected_addons) && b.selected_addons) ||
    (Array.isArray(b.selectedAddons) && b.selectedAddons) ||
    [];
  const hasAddons = selectedAddons.length > 0;

  const addonsTotalRaw =
    b.addons_total_amount ?? b.addonsTotal ?? b.addons_totalAmount ?? null;

  const addonsTotal =
    addonsTotalRaw != null
      ? Number(addonsTotalRaw)
      : hasAddons
      ? selectedAddons.reduce((sum, a) => {
          if (!a) return sum;
          const lt = Number(a.line_total ?? a.lineTotal ?? 0);
          if (!Number.isNaN(lt)) return sum + lt;
          return sum;
        }, 0)
      : null;

  // -----------------------------
  // Derived display fields
  // -----------------------------
  const salonName = getSalonName(b);
  const boothName = getBoothName(b);
  const status = getStatus(b);
  const isCancelled = status === "cancelled";

  const statusLabel = isCancelled ? "Cancelled" : "Confirmed";
  const statusClasses = isCancelled
    ? "bg-red-50 text-red-700 border border-red-200"
    : "bg-emerald-50 text-emerald-700 border border-emerald-200";

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <div className="border rounded-lg p-4 shadow-sm bg-white">
      <div className="flex justify-between items-start gap-2 mb-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            {salonName}
          </div>
          <div className="text-xs text-slate-600">{boothName}</div>
        </div>

        <div className="flex flex-wrap gap-1 justify-end">
          {/* Status badge */}
          <span
            className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusClasses}`}
          >
            {statusLabel}
          </span>

          {/* Host-only context label (NEVER for client/tech/admin) */}
          {hostContextLabel && (
            <div className="inline-flex px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[11px] font-medium">
              {hostContextLabel}
            </div>
          )}

          {/* Universal "My booking" badge with strict rule */}
          {isMyBooking && (
            <div className="inline-flex px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-medium">
              My booking
            </div>
          )}
        </div>
      </div>

      <div className="text-xs text-slate-700 space-y-1">
        <div>
          <span className="font-semibold">Start:</span>{" "}
          {formatDateTime(b.start_time ?? b.startTime)}
        </div>
        <div>
          <span className="font-semibold">End:</span>{" "}
          {formatDateTime(b.end_time ?? b.endTime)}
        </div>
        <div>
          <span className="font-semibold">Total:</span>{" "}
          {formatMoney(b.total_amount ?? b.total ?? b.totalAmount)}
        </div>

        {hasAddons && (
          <div>
            <span className="font-semibold">Add-ons:</span>{" "}
            {selectedAddons
              .map((a) => a?.label || a?.name || a?.key)
              .filter(Boolean)
              .join(", ")}
            {addonsTotal != null && (
              <span className="ml-1 text-[11px] text-slate-500">
                (+ {formatMoney(addonsTotal)})
              </span>
            )}
          </div>
        )}
      </div>

      {showCancelButton && onCancel && (
        <div className="mt-3">
          <button
            type="button"
            onClick={onCancel}
            className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded"
          >
            Cancel booking
          </button>
        </div>
      )}
    </div>
  );
}
